import React, { useRef, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
// import './Search.css'; // Removed import
import SanctionsReport, { SanctionsReportStandalone } from './SanctionsReport';
import { generatePDF, calculateExpiryDate, formatDate } from '../utils/pdfGenerator';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { collection, addDoc, query, where, getDocs, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { getActiveSanctionsCount } from '../services/searchService';

const ClearResults = ({ searchTerm, stats, searchId, entityType = 'person' }) => {
  // Get current theme
  const { theme } = useTheme();
  // Get translations
  const t = useTranslation();
  // Get current language
  const { language } = useLanguage();
  // Get toast functions
  const { showSuccess, showError } = useToast();
  // Get current user and organization
  const { currentUser, currentOrg } = useAuth();
  
  // State to store the entity type determined for these specific results
  const [determinedEntityType, setDeterminedEntityType] = useState(entityType);
  const [hasStatsProcessed, setHasStatsProcessed] = useState(false);

  useEffect(() => {
    if (stats && !hasStatsProcessed) {
      // Stats are available, and we haven't locked the entity type yet.
      // Prioritize stats.entityType, fallback to the entityType prop at this moment.
      setDeterminedEntityType(stats.entityType || entityType);
      setHasStatsProcessed(true);
    } else if (!stats) {
      // Stats are not available (e.g., results cleared, new search pending)
      // Reset the flag and allow determinedEntityType to follow the current prop.
      setHasStatsProcessed(false);
      setDeterminedEntityType(entityType);
    }
    // If stats are present and hasStatsProcessed is true, do nothing (type is locked).
  }, [stats, entityType, hasStatsProcessed]);
  
  // Determine display subject type based on the latched determinedEntityType and searchTerm fallback
  const baseEntityTypeForDisplay = (determinedEntityType || '').toLowerCase();
  const isCompanyByNamePattern = /\b(LLC|Ltd|Inc|Corp|GmbH|Co\.|Company|Limited|Corporation|Group|Holdings|Technologies|Solutions|International|Enterprises|Partners|Associates)\b/i.test(searchTerm);

  let subjectTypeForDisplay;
  if (baseEntityTypeForDisplay === 'enterprise' || 
      baseEntityTypeForDisplay === 'company' || 
      baseEntityTypeForDisplay === 'organization' || 
      baseEntityTypeForDisplay === 'entity') {
    subjectTypeForDisplay = 'entity';
  } else if (isCompanyByNamePattern && (baseEntityTypeForDisplay === 'person' || !baseEntityTypeForDisplay)) {
    subjectTypeForDisplay = 'entity'; // Fallback: if type is person/unknown but name suggests company
  } else {
    subjectTypeForDisplay = 'individual';
  }
  
  // State to track if this search is monitored
  const [isMonitored, setIsMonitored] = useState(false);
  // State for button loading
  const [loading, setLoading] = useState(false);
  // State to track if the component is ready to render
  const [isReady, setIsReady] = useState(false);
  // State to store the total active sanctions count
  const [totalActiveSanctions, setTotalActiveSanctions] = useState(0);
  
  // Create a ref for the report
  const reportRef = useRef(null);
  
  // Get search date
  const searchDate = new Date(stats?.searchTime || Date.now());
  
  // Format dates
  const searchTimeFormatted = formatDate(searchDate);
  const expiryDateFormatted = calculateExpiryDate(searchDate);
  
  // Use all three main sources we know exist in the database
  const sources = ["EU Sanctions", "OFAC Sanctions", "UN Sanctions"];

  // Fetch total active sanctions count on component mount
  useEffect(() => {
    const fetchTotalSanctions = async () => {
      try {
        const count = await getActiveSanctionsCount();
        setTotalActiveSanctions(count);
      } catch (error) {
        console.error("Error fetching total sanctions count:", error);
        // Use a fallback value or stats.totalDocumentsQueried
        setTotalActiveSanctions(stats?.totalDocumentsQueried || 6200);
      }
    };
    
    fetchTotalSanctions();
  }, [stats?.totalDocumentsQueried]); // Added stats dependency if used as fallback

  // Ensure we have minimum required data before rendering
  useEffect(() => {
    if (searchTerm && stats) {
      setIsReady(true);
    }
  }, [searchTerm, stats]);

  // Check if this search is already monitored on component mount
  useEffect(() => {
    const checkMonitoringStatus = async () => {
      if (!stats?.verificationId || !currentUser || !currentOrg) return;
      
      try {
        const q = query(
          collection(db, "monitoring"), 
          where("verificationId", "==", stats.verificationId),
          where("orgId", "==", currentOrg.id)
        );
        
        const querySnapshot = await getDocs(q);
        setIsMonitored(!querySnapshot.empty);
      } catch (error) {
        console.error("Error checking monitoring status:", error);
      }
    };
    
    checkMonitoringStatus();
  }, [stats?.verificationId, currentUser, currentOrg]);

  // Handle adding to monitoring
  const handleAddToMonitoring = async () => {
    if (!stats?.verificationId) {
      showError(t('monitoring.missingVerificationId'));
      return;
    }
    
    if (!currentUser || !currentOrg) {
      showError(t('errors.loginRequired'));
      return;
    }
    
    setLoading(true);
    
    try {
      await addDoc(collection(db, "monitoring"), {
        searchTerm,
        verificationId: stats.verificationId,
        lastSearchDate: serverTimestamp(),
        createdAt: serverTimestamp(),
        userId: currentUser.uid,
        orgId: currentOrg.id
      });
      
      setIsMonitored(true);
      showSuccess(t('monitoring.addedSuccess'));
    } catch (error) {
      console.error("Error adding to monitoring:", error);
      showError(t('monitoring.addError'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle removing from monitoring
  const handleRemoveFromMonitoring = async () => {
    if (!stats?.verificationId || !currentUser || !currentOrg) return;
    
    setLoading(true);
    
    try {
      const q = query(
        collection(db, "monitoring"), 
        where("verificationId", "==", stats.verificationId),
        where("orgId", "==", currentOrg.id)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Delete all matching documents (should only be one)
      const deletePromises = [];
      querySnapshot.forEach((doc) => {
        deletePromises.push(deleteDoc(doc.ref));
      });
      
      await Promise.all(deletePromises);
      
      setIsMonitored(false);
      showSuccess(t('monitoring.removedSuccess'));
    } catch (error) {
      console.error("Error removing from monitoring:", error);
      showError(t('monitoring.removeError'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle PDF download
  const handleDownloadPDF = async () => {
    try {
      // Show loading state
      const loadingIndicator = document.getElementById('pdf-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.style.display = 'flex';
      }

      // Prepare data for the report using the determinedEntityType
      const reportData = {
        name: searchTerm,
        verificationId: stats?.verificationId || 'SCR-000000',
        timestamp: searchTimeFormatted,
        recordsScanned: totalActiveSanctions || stats?.totalDocumentsQueried || 0,
        sources: sources,
        expiryDate: expiryDateFormatted,
        issuedBy: "Protego",
        entityType: determinedEntityType, // Use the latched entity type for the PDF report
        runBy: currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
        runByEmail: currentUser?.email || ''
      };
      
      // Create a temporary div for rendering the report
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '800px'; // Best width for A4 format
      tempDiv.style.height = '1120px'; // Best height for A4 format (approximately)
      document.body.appendChild(tempDiv);
      
      // Create a root using the client API
      const tempRoot = createRoot(tempDiv);
      
      // Create a promise to handle the rendering and PDF generation
      await new Promise((resolve) => {
        // Use the standalone version that doesn't depend on React hooks
        tempRoot.render(<SanctionsReportStandalone data={reportData} language={language} ref={reportRef} />);
        
        // Allow time for the report to render
        setTimeout(async () => {
          try {
            // Generate PDF from the rendered component with proper search timestamp
            await generatePDF(
              tempDiv.firstChild, 
              `sanctions-report-${reportData.name.replace(/\s+/g, '-')}.pdf`,
              {
                searchTimestamp: stats?.searchTime || searchDate,
                searchType: determinedEntityType === 'enterprise' ? 'Enterprise Search' : 'People Search',
                recordsScanned: totalActiveSanctions || stats?.totalDocumentsQueried || 0,
                runBy: currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
                runByEmail: currentUser?.email || '',
                organizationName: currentOrg?.name || currentOrg?.displayName || '',
                language: language
              }
            );
          } finally {
            // Clean up
            tempRoot.unmount();
            document.body.removeChild(tempDiv);
            resolve();
          }
        }, 500); // Give it more time to fully render
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Handle error - show message to user
      alert('There was an error generating the PDF report. Please try again.');
    } finally {
      // Hide loading state
      const loadingIndicator = document.getElementById('pdf-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.style.display = 'none';
      }
    }
  };

  // If we don't have sufficient data yet, show a loading placeholder
  // This prevents flashing of incomplete content
  if (!isReady) {
    return (
      <div className="bg-white dark:bg-gray-900 p-6 rounded-lg">
        <div className="flex flex-col items-center justify-center text-gray-600 dark:text-gray-300 p-8">
          <h3 className="text-lg font-medium">{t('results.preparingResults')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg">
      {/* Main Result Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-16 h-16 flex items-center justify-center bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full mb-4">
            <i className="fa-solid fa-circle-check text-3xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('results.noSanctionsFound')}</h2>
          <p className="text-base text-gray-600 dark:text-gray-300">
            <strong className="font-semibold text-blue-600 dark:text-blue-400">{searchTerm}</strong> {t(`results.cleared${subjectTypeForDisplay === 'entity' ? 'Entity' : ''}`)}
          </p>
        </div>
        
        <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('results.verificationId')}</div>
              <div className="text-base font-semibold text-gray-900 dark:text-white font-mono">{stats?.verificationId || 'SCR-000000'}</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('results.recordsScanned')}</div>
              <div className="text-base font-semibold text-gray-900 dark:text-white">{totalActiveSanctions?.toLocaleString() || stats?.totalDocumentsQueried?.toLocaleString() || '0'}</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('results.searchTimestamp')}</div>
              <div className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center md:justify-start gap-2">
                <i className="fa-solid fa-clock text-gray-500 dark:text-gray-400"></i>
                <span>{searchTimeFormatted}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <button 
          className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
          onClick={handleDownloadPDF}
        >
          <i className="fa-solid fa-download"></i>
          {t('results.downloadPdfReport')}
        </button>
        
        <button 
          className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 font-medium rounded-lg transition-colors duration-200
            ${isMonitored 
              ? 'bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/30 text-red-700 dark:text-red-400' 
              : 'bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-400'}`}
          onClick={isMonitored ? handleRemoveFromMonitoring : handleAddToMonitoring}
          disabled={loading}
        >
          <i className={`fa-solid ${isMonitored ? 'fa-eye-slash' : 'fa-shield'}`}></i>
          {loading ? (
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-circle-notch fa-spin"></i>
              {t('common.loading')}
            </span>
          ) : (
            isMonitored ? t('results.removeFromMonitoring') : t('results.addToMonitoring')
          )}
        </button>
      </div>
      
      {/* Sources Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
          <i className="fa-solid fa-database text-blue-600 dark:text-blue-400"></i>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('results.sourcesChecked')}</h3>
        </div>
        
        <div className="grid sm:grid-cols-3 gap-4">
          {/* EU Sanctions */}
          <div className="flex flex-col p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
                <i className="fa-solid fa-flag"></i>
              </div>
              <div className="font-medium text-gray-900 dark:text-white">EU Sanctions</div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300 pl-13">
              Travel Bans and Financial Sanctions
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 pl-13">{t('results.databaseChecked')}</div>
          </div>
          
          {/* OFAC Sanctions */}
          <div className="flex flex-col p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full">
                <i className="fa-solid fa-shield"></i>
              </div>
              <div className="font-medium text-gray-900 dark:text-white">OFAC Sanctions</div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300 pl-13">
              SDN and Non-SDN Lists
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 pl-13">{t('results.databaseChecked')}</div>
          </div>
          
          {/* UN Sanctions */}
          <div className="flex flex-col p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                <i className="fa-solid fa-globe"></i>
              </div>
              <div className="font-medium text-gray-900 dark:text-white">UN Sanctions</div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300 pl-13">
              Security Council Consolidated List
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 pl-13">{t('results.databaseChecked')}</div>
          </div>
        </div>
      </div>
      
      {/* Disclaimer */}
      <div className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg mb-6">
        <p className="mb-2">{t('results.disclaimer')}</p>
        <p>{t('results.legalNote')}</p>
      </div>
      
      {/* Hidden loading indicator for PDF generation */}
      <div 
        id="pdf-loading-indicator" 
        className="fixed inset-0 bg-black/50 z-50 hidden items-center justify-center"
      >
        <div className="flex flex-col items-center bg-white dark:bg-gray-800 p-6 rounded-lg">
          <i className="fa-solid fa-circle-notch fa-spin fa-2x text-blue-600 dark:text-blue-400 mb-3"></i>
          <span className="text-gray-900 dark:text-white">{t('results.generatingPdfReport')}</span>
        </div>
      </div>
    </div>
  );
};

// Helper to format source name
const formatSourceName = (source) => {
  return source
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default ClearResults;